import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';
import md5 from 'crypto-js/md5';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { getLastRecordedWeight } from '../../lib/animalWeight';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import PrescriptionPackageTotal from './prescriptionPackageTotal';
import PrescriptionAiAssistant from './prescriptionAiAssistant';

// Helper to parse ISO 8601 duration format (P1Y, P10D, P5D, etc.)
function parseValidityDuration(duration) {
    if (!duration) return '';
    const match = duration.match(/^P(\d+)([YMWD])$/);
    if (!match) return duration;
    const [, num, unit] = match;
    const units = { Y: 'år', M: 'måneder', W: 'uker', D: 'dager' };
    return `${num} ${units[unit] || unit}`;
}

function getWeightRange(weight) {
    if (!weight) return null;
    if (weight < 5) return '0-5';
    if (weight < 10) return '5-10';
    if (weight < 20) return '10-20';
    if (weight < 35) return '20-35';
    if (weight < 50) return '35-50';
    return '50+';
}

@withText(props => ({
    drugTitle: <Text id='dyrejournal.drug.search-title'>Search for drug</Text>,
    newDrugTitle: <Text id='dyrejournal.drug.new-title'>New drug</Text>,
}))
@observer
class AddPrescription extends Component {
	constructor(props) {
        super(props);
        this.state = {
            sections: fields.get('prescription'),
            sectionDrugs: fields.get('prescriptionDrugs'),
            currentAnimal: null,
            animalPrescriptions: [],
            historyExpanded: false,
        };
        this.searchTimer = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { id, isNew } = props;
        const { drugStore, saleStore, prescriptionStore } = this.props.stores;
        const { newSale } = saleStore;
        // await drugStore.load();
        await prescriptionStore.loadVeterinaries();
        await this.loadPrescription(id, isNew);
    }

    loadPrescription = async (id, isNew) => {
        const { journalStore, prescriptionStore, animalStore, userStore } = this.props.stores;
        const {
            journalId: propJournalId,
            animalId: propAnimalId,
            saleId: propSaleId,
        } = this.props;
        let animalId = null;

        if (isNew) {
            const { user } = userStore;
            const { newJournal } = journalStore;
            // Default the prescribing vet to the journal's vet, falling back to
            // the logged-in user (mirrors newJournal.js) so the vet is pre-filled
            // instead of forcing a manual pick on every new prescription.
            const veterinary = newJournal.veterinary || user.id;
            // Prefer explicit drawer props over the observable so callers that
            // pre-create the journal (e.g. listAnimalMini "New prescription")
            // can't be defeated by an intervening saveSale that resets newJournal.
            const journal = propJournalId || newJournal.id;
            const animal = propAnimalId || newJournal.animal;
            const sale = propSaleId || newJournal.sale;
            prescriptionStore.updateObjectKeyValue('newPrescription', 'date', new Date());
            prescriptionStore.updateObjectKeyValue('newPrescription', 'veterinary', veterinary);
            prescriptionStore.updateObjectKeyValue('newPrescription', 'journal', journal);
            prescriptionStore.updateObjectKeyValue('newPrescription', 'animal', animal);
            prescriptionStore.updateObjectKeyValue('newPrescription', 'sale', sale);
            animalId = animal;
        } else {
            const newPrescription = await prescriptionStore.load(parseInt(id, 10), false, { skipUpdate: true });
            prescriptionStore.updateKeyValue('newPrescription', newPrescription[0]);
            animalId = newPrescription[0]?.animal;
        }

        // Load animal data for species-based recommendations
        if (animalId) {
            const animals = await animalStore.load(animalId, false, { skipUpdate: true });
            if (animals && animals.length > 0) {
                const animal = animals[0];
                this.setState({ currentAnimal: animal });

                // Load prescriptionDrugTexts for this species to show recommendation chips
                if (animal.speciesId) {
                    const { drugStore } = this.props.stores;
                    const response = await util.fetchApi('/api/drugs/', { method: 'GET' }, {
                        search: '',
                        addData: 'prescriptionDrugTexts',
                        speciesId: animal.speciesId,
                    });
                    if (response?.included?.prescriptionDrugTexts) {
                        drugStore.updateKeyValue('prescriptionDrugTexts', response.included.prescriptionDrugTexts);
                    }
                }
            }

            // Load prescription history for this animal
            const historyResponse = await prescriptionStore.load({
                query: { animal: [animalId] },
                skipUpdate: true,
            });
            const history = historyResponse || [];
            // Exclude current prescription if editing
            const { newPrescription } = prescriptionStore;
            const filteredHistory = newPrescription.id
                ? history.filter(p => p.id !== newPrescription.id)
                : history;
            this.setState({
                animalPrescriptions: filteredHistory,
                historyExpanded: filteredHistory.length <= 3,
            });
        }
    }

    savePrescription = async e => {
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;

        if (newPrescription.id) {
            await prescriptionStore.save(newPrescription);
        } else {
            const response = await prescriptionStore.insert(newPrescription);
            // Update current newPrescription object
            const prescription = response?.data?.prescription;
            prescriptionStore.updateObjectKeyValue('newPrescription', 'id', prescription?.id);
        }
        return true;
    }

    fetchDrugPrefill = async (lId) => {
        const { drugStore, userStore } = this.props.stores;
        const { customer } = userStore;
        const { featureFlags = {} } = customer || {};
        const { currentAnimal } = this.state;

        // Primary: backend cascading match (feature-flag gated)
        if (featureFlags.prefillPrescriptionFields && currentAnimal) {
            const prefill = await drugStore.fetchPrefillSuggestion({
                lId,
                speciesId: currentAnimal.speciesId,
                breedId: currentAnimal.breedId,
                weightRange: getWeightRange(currentAnimal.weight),
            });
            if (prefill) return prefill;
        }

        // Fallback: local prescriptionDrugTexts with cascading match client-side
        const { prescriptionDrugTexts = [] } = drugStore;
        const sId = currentAnimal?.speciesId;
        const bId = currentAnimal?.breedId;
        const wr = getWeightRange(currentAnimal?.weight);
        const candidates = prescriptionDrugTexts.filter(r => r.lId === lId);
        if (candidates.length === 0) return null;

        return (
            candidates.find(r => r.speciesId === sId && r.breedId === bId && r.weightRange === wr)
            || candidates.find(r => r.speciesId === sId && r.weightRange === wr)
            || candidates.find(r => r.speciesId === sId && r.breedId === bId)
            || candidates.find(r => r.speciesId === sId)
            || candidates[0]
        );
    }

    searchDrugCallback = async (selectedItem) => {
        const { prescriptionStore, drugStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const { drugs = [] } = newPrescription;

        // Unified prefill lookup (matches onClickRecommendation behavior)
        const rec = selectedItem.lId ? await this.fetchDrugPrefill(selectedItem.lId) : null;

        // Dosage: prefer historical dosage; fall back to kortDose lookup from drug metadata
        let dosage = rec?.dosage || '';
        if (!dosage && selectedItem.kortDose && selectedItem.kortDose.length > 0) {
            dosage = selectedItem.kortDose
                .map(v => drugStore.findKortDose(v.v))
                .filter(Boolean)
                .join(', ');
        }

        const rpText = rec?.rpText || (selectedItem.navnFormStyrke || '').trim();

        prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', [...drugs, {
            md5: md5(JSON.stringify(newPrescription)).toString(),
            ...selectedItem,
            rpText,
            qty: rec?.qty || 1,
            dosage,
            frequency: rec?.frequency || '',
            diagnoseText: rec?.diagnoseText || '',
            usageText: rec?.usageText || '',
            additionalNotes: rec?.additionalNotes || '',
            reit: rec?.reit || '',
        }]);
    }

    onInputDrugInfo = e => {
        const { name, value } = e.target;
        const { md5 } = e.target.dataset;
        // console.log('onInputDrugInfo', { name, value, md5 });
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const idx = newPrescription.drugs.findIndex(v => v.md5 === md5);
        prescriptionStore.updateObjectKeyArrFieldValue('newPrescription', 'drugs', idx, name, value);
    }

    inputDrugInfo = ({ name, value, md5 }) => {
        console.log('inputDrugInfo', { name, value, md5 });
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const idx = newPrescription.drugs.findIndex(v => v.md5 === md5);
        prescriptionStore.updateObjectKeyArrFieldValue('newPrescription', 'drugs', idx, name, value);
    }

    onClickCreatePrescription = async e => {
        // console.log('onClickCreatePrescription', e);
        const { callback = () => {} } = this.props;
        const { prescriptionStore } = this.props.stores;
        await this.savePrescription();
        const { newPrescription } = prescriptionStore;
        callback({
            ...newPrescription,
            md5: md5(JSON.stringify(newPrescription)).toString(),
        });
        prescriptionStore.updateKeyValue('newPrescription', {});
    }

    onClickNewDrug = e => {
        const { drawerLevel = 1 } = this.props;
		const { appState, saleStore, animalStore } = this.props.stores;
		const { drawerHeightMedium } = appState;

		appState.openDrawer('newDrug', {
			height: drawerHeightMedium,
            isNew: true,
            hideAdmin: true,
            callback: drug => {
                appState.toggleDrawer(false, drawerLevel + 1);
            },
		}, drawerLevel + 1);
    }

    onClickRecommendation = async (e) => {
        const { lId } = e.target.closest('.recommendation-chip').dataset;
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const { drugs = [] } = newPrescription;

        // Fetch the full drug object by lId (not via search — lId is not a search field)
        const response = await util.fetchApi('/api/drugs/', { method: 'GET' }, { lId });
        const drug = response?.data?.[0];
        if (!drug) return;

        // Unified prefill lookup (matches searchDrugCallback behavior)
        const rec = await this.fetchDrugPrefill(lId);

        const rpText = rec?.rpText || drug.navnFormStyrke || '';
        prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', [...drugs, {
            md5: md5(JSON.stringify(newPrescription)).toString(),
            ...drug,
            rpText,
            qty: rec?.qty || 1,
            dosage: rec?.dosage || '',
            frequency: rec?.frequency || '',
            diagnoseText: rec?.diagnoseText || '',
            usageText: rec?.usageText || '',
            additionalNotes: rec?.additionalNotes || '',
            reit: rec?.reit || '',
        }]);
    }

    onClickToggleHistory = () => {
        this.setState(prev => ({ historyExpanded: !prev.historyExpanded }));
    }

    onClickCopyHistoryDrug = async (drug) => {
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const { drugs = [] } = newPrescription;

        // Strip fields that must not carry over from the source prescription
        const { md5: _oldMd5, prescriptionDate, ...drugFields } = drug;

        prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', [...drugs, {
            ...drugFields,
            md5: md5(JSON.stringify(newPrescription)).toString(),
        }]);
    }

    // AI-resept: add a resolved FEST drug with the proposal's prefill. Same line shape as
    // onClickRecommendation so the rest of the drawer (edit, remove, save) is unchanged.
    onAcceptAiProposal = async (drug, prefill = {}) => {
        const { prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        const { drugs = [] } = newPrescription;
        if (!drug || !drug.lId) {
            return;
        }
        prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', [...drugs, {
            md5: md5(JSON.stringify(newPrescription)).toString(),
            ...drug,
            rpText: drug.navnFormStyrke || '',
            qty: prefill.qty || 1,
            dosage: prefill.dosage || '',
            frequency: prefill.frequency || '',
            diagnoseText: prefill.diagnoseText || '',
            usageText: prefill.usageText || '',
            additionalNotes: prefill.additionalNotes || '',
            reit: prefill.reit || '',
        }]);
    }

    // onClickRemoveDrug = e => {
    //     // console.log('onClickRemoveDrug', e);
    //     const { id } = e.target.closest('drugs').dataset;
    //     const { prescriptionStore } = this.props.stores;
    //     const { newPrescription } = prescriptionStore;
    //     const { drugs } = newPrescription;
    //     const newDrugs = drugs.filter(v => v.id !== parseInt(id, 10));
    //     prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', newDrugs);
    // }

    onClickRemoveDrug = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { prescription, id, md5 } = e.target.closest('.drug').dataset;

        // console.log('onClickRemovePrescription', { journal, id, md5 });
        const { journalStore, prescriptionStore } = this.props.stores;
        const { newPrescription } = prescriptionStore;
        // prescriptionStore.removeArrayObject({ field: 'drugs', id: prescription, md5 });
        const { drugs } = newPrescription;
        const newDrugs = drugs.filter(p => p.md5 !== md5);
        prescriptionStore.updateObjectKeyValue('newPrescription', 'drugs', newDrugs);
        // await this.savePrescription();
    }

    componentDidMount() {
        this.loadAll();
    }

    render() {
        const {
            sections,
            sectionDrugs,
            currentAnimal,
            animalPrescriptions,
            historyExpanded,
        } = this.state;
        const {
            newDrugTitle,
            drugTitle,
            drawerLevel = 1,
            isNew,
            hideAdmin,
            saleIsLocked,
        } = this.props;
        const { userStore, drugStore, prescriptionStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const { customer } = userStore;
        const { featureFlags = {} } = customer || {};
        const aiPrescriptionEnabled = parseInt(featureFlags.aiPrescription, 10) === 1;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { newPrescription } = prescriptionStore;
        const { drugs } = newPrescription;

        // A veterinary (user.id) must be selected before a prescription can be created/saved.
        const hasVeterinary = Number(newPrescription.veterinary) > 0;

        // Always show the animal's last recorded weight in the drawer
        // (TICKET-01286) — before, weight was only visible in the
        // "Ofte brukt for" header when species suggestions happened to exist.
        const { weight: lastWeight, date: lastWeightDate } = getLastRecordedWeight(currentAnimal);

        // Get drug recommendations based on species and weight range
        const { prescriptionDrugTexts = [] } = drugStore;
        const animalWeightRange = currentAnimal?.weight ? getWeightRange(currentAnimal.weight) : null;
        const speciesRecommendations = currentAnimal?.speciesId
            ? prescriptionDrugTexts
                .filter(rec => rec.speciesId === currentAnimal.speciesId
                    && rec.drugName
                    && (!animalWeightRange || !rec.weightRange || rec.weightRange === animalWeightRange))
                .sort((a, b) => (b.total || 0) - (a.total || 0))
                .filter((rec, idx, arr) => arr.findIndex(r => r.lId === rec.lId) === idx)
                .slice(0, 8)
            : [];

        // Build animal-specific drug suggestions from prescription history
        const animalDrugSuggestions = animalPrescriptions.length > 0
            ? animalPrescriptions
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .flatMap(p => (p.drugs || []).map(d => ({ ...d, prescriptionDate: p.date })))
                .filter((drug, idx, arr) => drug.lId && arr.findIndex(d => d.lId === drug.lId) === idx)
                .slice(0, 8)
            : [];

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-bottom: 100px;'>

                    <div class='w-100 d-flex flex-sm-row flex-column'>
                        <div class='w-100 d-flex flex-column'>
                            <div class='font-weight-lighter px-3 py-2 box-header d-flex flex-row justify-content-between'>
                                <Text id='dyrejournal.prescription.add'>Add prescription</Text>
                            </div>

                            {currentAnimal && <div class='mx-3 mt-2 px-3 py-2 rounded bg-body-secondary'>
                                <div class='d-flex flex-row align-items-center flex-wrap'>
                                    <div class='flex-fill'>
                                        <i class='fa-duotone fa-paw me-1 text-body-secondary' />
                                        <strong>{currentAnimal.name}</strong>
                                        {(currentAnimal.species || currentAnimal.breed) && <span class='text-body-secondary ms-1'>({currentAnimal.species}{currentAnimal.breed ? `/${currentAnimal.breed}` : ''})</span>}
                                    </div>
                                    {!!lastWeight && <div class='ms-2'>
                                        <i class='fa-duotone fa-weight-scale me-1 text-body-secondary' />
                                        <Text id='dyrejournal.last-recorded-weight'>Last recorded weight</Text>:
                                        {' '}<strong>{lastWeight} kg</strong>
                                        {!!lastWeightDate && <span class='text-body-secondary ms-1'>
                                            ({new Date(lastWeightDate).toLocaleString('nb-NO', { day: '2-digit', month: 'short', year: '2-digit' })})
                                        </span>}
                                    </div>}
                                </div>
                            </div>}

                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-1'>
                                <FormFieldsCompact
                                    stores={this.props.stores}
                                    sections={sections}
                                    holdingStore={prescriptionStore}
                                    objectName={'newPrescription'}
                                    isNew={newPrescription.id ? false : true}
                                    hideAdmin={hideAdmin}
                                    drawerLevel={drawerLevel}
                                />
                            </div>

                            <div class='font-weight-lighter text-muted mx-4 mt-1'>
                                <small class='text-uppercase ms-3'>
                                    <Text id='dyrejournal.prescription.drugs'>Prescription drugs</Text>
                                </small>
                            </div>

                            <div class={`d-flex flex-column mx-4 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {/* Animal prescription history */}
                                {animalPrescriptions.length > 0 && (
                                    <div class='mb-3 p-2 border rounded' style='background-color: rgba(108,117,125,0.05);'>
                                        <div
                                            class='small text-muted mb-2 d-flex justify-content-between align-items-center'
                                            style='cursor: pointer;'
                                            onClick={this.onClickToggleHistory}
                                        >
                                            <span>
                                                <i class='fa-solid fa-history me-1' />
                                                <Text id='dyrejournal.prescription.previousPrescriptions'>Previous prescriptions for</Text>
                                                {' '}{currentAnimal?.name || currentAnimal?.species || ''}
                                                {' '}({animalPrescriptions.length})
                                            </span>
                                            <i class={`fa-solid fa-chevron-${historyExpanded ? 'up' : 'down'}`} />
                                        </div>
                                        {historyExpanded && (
                                            <div class='d-flex flex-column gap-2' style='max-height: 300px; overflow-y: auto;'>
                                                {animalPrescriptions.map((prescription, pIdx) => (
                                                    <div key={prescription.id || pIdx} class='p-2 border rounded bg-body'>
                                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                                            <small class='text-body-secondary'>
                                                                {util.formatDate(prescription.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                                                            </small>
                                                        </div>
                                                        {prescription.drugs && prescription.drugs.length > 0 && (
                                                            <div class='d-flex flex-column gap-1'>
                                                                {prescription.drugs.map((drug, dIdx) => (
                                                                    <div key={dIdx} class='d-flex justify-content-between align-items-center'>
                                                                        <small>
                                                                            {drug.qty || 1} x {drug.rpText || drug.navnFormStyrke}
                                                                            {drug.dosage && <span class='text-body-secondary'> - {drug.dosage}</span>}
                                                                        </small>
                                                                        {!saleIsLocked && (
                                                                            <button
                                                                                type='button'
                                                                                class='btn btn-sm btn-outline-secondary py-0 px-1'
                                                                                onClick={() => this.onClickCopyHistoryDrug(drug)}
                                                                                title='Copy to current prescription'
                                                                            >
                                                                                <i class='fa-solid fa-plus' style='font-size: 0.7em;' />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Animal-specific drug suggestions */}
                                {animalDrugSuggestions.length > 0 && (
                                    <div class='mb-3 p-2 border rounded' style='background-color: rgba(25,135,84,0.05);'>
                                        <div class='small text-muted mb-2'>
                                            <i class='fa-solid fa-paw me-1' />
                                            <Text id='dyrejournal.prescription.previouslyUsedFor'>Previously used for</Text>
                                            {' '}{currentAnimal?.name || ''}
                                        </div>
                                        <div class='d-flex flex-wrap gap-1'>
                                            {animalDrugSuggestions.map((drug, idx) => (
                                                <button
                                                    key={drug.lId + idx}
                                                    type='button'
                                                    class='recommendation-chip btn btn-sm btn-outline-success rounded-pill'
                                                    onClick={() => this.onClickCopyHistoryDrug(drug)}
                                                    disabled={saleIsLocked}
                                                    style='font-size: 0.8em;'
                                                >
                                                    {drug.rpText || drug.navnFormStyrke || drug.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Species-based drug recommendations */}
                                {speciesRecommendations.length > 0 && (
                                    <div class='mb-3 p-2 border rounded' style='background-color: rgba(0,123,255,0.05);'>
                                        <div class='small text-muted mb-2'>
                                            <i class='fa-solid fa-star me-1' />
                                            <Text id='dyrejournal.prescription.frequentlyUsed'>Ofte brukt for</Text>
                                            {' '}{currentAnimal?.species || currentAnimal?.speciesName}
                                            {currentAnimal?.weight > 0 && <> ({currentAnimal.weight} kg)</>}
                                        </div>
                                        <div class='d-flex flex-wrap gap-1'>
                                            {speciesRecommendations.map((rec, idx) => (
                                                <button
                                                    key={rec.lId + idx}
                                                    type='button'
                                                    class='recommendation-chip btn btn-sm btn-outline-primary rounded-pill'
                                                    data-l-id={rec.lId}
                                                    onClick={this.onClickRecommendation}
                                                    disabled={saleIsLocked}
                                                    style='font-size: 0.8em;'
                                                >
                                                    {rec.drugName}
                                                    <span class='badge bg-secondary ms-1'>{rec.total}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {aiPrescriptionEnabled && currentAnimal && (
                                    <PrescriptionAiAssistant
                                        stores={this.props.stores}
                                        animal={currentAnimal}
                                        journalId={newPrescription.journal}
                                        drugs={drugs || []}
                                        saleIsLocked={saleIsLocked}
                                        onAcceptProposal={this.onAcceptAiProposal}
                                    />
                                )}

                                <DyrejournalSaleSearch
                                    stores={this.props.stores}
                                    key={'drugs'}
                                    addData={['prescriptionDrugTexts']}
                                    title={drugTitle}
                                    holdingStore={drugStore}
                                    searchResultList={'searchResults'}
                                    filterFields={[
                                        { field: 'legemiddelformKortDn', label: 'Form' },
                                        { field: 'pakningsinfoPakningsstr', label: 'Pakning', formatValue: v => `${v} stk` },
                                    ]}
                                    searchResultsLine={(e, idx, selectedIndex) => {
                                        return(<>
                                            <div class={`d-flex flex-column px-3 py-2 ${idx === selectedIndex ? 'selected' : ''}`}>
                                                <div class='d-flex flex-row justify-content-between align-items-start'>
                                                    <div class='flex-fill'>
                                                        <strong>{e.navnFormStyrke}</strong>
                                                    </div>
                                                    <div class='d-flex flex-row align-items-center gap-1 ms-2'>
                                                        {e.legemiddelformKortDn && <span class='badge bg-body-secondary text-body-secondary' style='font-size: 0.7em; white-space: nowrap;'>
                                                            {e.legemiddelformKortDn}
                                                        </span>}
                                                        {e.pakningsinfoPakningsstr && <span class='badge bg-info-subtle text-info-emphasis' style='font-size: 0.7em; white-space: nowrap;'>
                                                            <i class='fa-duotone fa-box me-1' />{e.pakningsinfoPakningsstr} {e.pakningsinfoEnhetPakningV}
                                                        </span>}
                                                        <span class='text-body-secondary small' style='white-space: nowrap;'>
                                                            {e.atcV}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class='d-flex flex-row flex-wrap text-body-secondary small'>
                                                    {e.varenr && <span>Varenr: {e.varenr}</span>}
                                                    {e.virkestoffNavn && <>
                                                        <span class='mx-1'>|</span>
                                                        <span>{e.virkestoffNavn}</span>
                                                    </>}
                                                    {e.prisAUP > 0 && <><span class='mx-1'>|</span><span>{e.prisAUP} {e.prisCurrency || 'NOK'}</span></>}
                                                </div>
                                            </div>
                                        </>);
                                    }}
                                    searchResultsFields={e => {
                                        return(<>
                                            <div class='d-flex flex-column px-3 py-2'>
                                                {/* Drug Name & Basic Info */}
                                                <div class='mb-3'>
                                                    <h6 class='mb-1'>{e.navnFormStyrke}</h6>
                                                    <div class='small text-muted'>
                                                        {e.preparattypeDn}
                                                        {e.reseptgruppeDn && <> · {e.reseptgruppeDn}</>}
                                                        {e.varenr && <> · Varenr: {e.varenr}</>}
                                                    </div>
                                                </div>

                                                {/* Active Ingredient */}
                                                {(e.virkestoffNavn || e.virkestoffNavnEngelsk) && <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.activeIngredient'>Virkestoff</Text>
                                                    </div>
                                                    <div>
                                                        {e.virkestoffNavn}
                                                        {e.virkestoffNavnEngelsk && <span class='text-muted'> ({e.virkestoffNavnEngelsk})</span>}
                                                    </div>
                                                    {e.virkestoffMedStyrke && e.virkestoffMedStyrke.length > 0 && (
                                                        <div class='small text-muted mt-1'>
                                                            {e.virkestoffMedStyrke.map((v, i) => (
                                                                <span key={i}>
                                                                    {i > 0 && ', '}
                                                                    {v.styrkeV} {v.styrkeU}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>}

                                                {/* Dosage Guidance */}
                                                {e.kortDose && e.kortDose.length > 0 && <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.dosageGuidance'>Doseringsveiledning</Text>
                                                    </div>
                                                    {e.kortDose.map((v, i) => (
                                                        <div key={i} class='mb-1'>
                                                            {drugStore.findKortDose(v.v) || v.dn}
                                                        </div>
                                                    ))}
                                                </div>}

                                                {/* Administration */}
                                                {(e.administrasjonsveiDn || e.forhandsregelInntakDn || e.delingDn) && <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.administration'>Administrering</Text>
                                                    </div>
                                                    <div class='small'>
                                                        {e.administrasjonsveiDn && <div><strong>Administrasjonsvei:</strong> {e.administrasjonsveiDn}</div>}
                                                        {e.enhetDoseringDn && <div><strong>Doseringsenhet:</strong> {e.enhetDoseringDn}</div>}
                                                        {e.forhandsregelInntakDn && <div><strong>Merk:</strong> {e.forhandsregelInntakDn}</div>}
                                                        {e.delingDn && <div><strong>Deling:</strong> {e.delingDn}</div>}
                                                        {e.administrasjonLegemiddelKanKnusesDn && e.administrasjonLegemiddelKanKnusesDn !== 'Ukjent' && (
                                                            <div><strong>Kan knuses:</strong> {e.administrasjonLegemiddelKanKnusesDn}</div>
                                                        )}
                                                    </div>
                                                </div>}

                                                {/* Package Info */}
                                                <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.packageInfo'>Pakningsinformasjon</Text>
                                                    </div>
                                                    <div class='small'>
                                                        <div><strong>Form:</strong> {e.legemiddelformKortDn || e.legemiddelformLang}</div>
                                                        <div><strong>Pakning:</strong> {e.pakningsinfoPakningsstr || e.pakningsinfoMengde} {e.pakningsinfoEnhetPakningDn} ({e.pakningsinfoPakningstypeDn})</div>
                                                        {e.smakDn && <div><strong>Smak:</strong> {e.smakDn}</div>}
                                                        {e.ean && <div><strong>EAN:</strong> {e.ean}</div>}
                                                    </div>
                                                </div>

                                                {/* Storage & Validity */}
                                                {(e.oppbevaringDn || e.reseptgyldighetVarighet) && <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.storageValidity'>Oppbevaring og gyldighet</Text>
                                                    </div>
                                                    <div class='small'>
                                                        {e.oppbevaringDn && <div><strong>Oppbevaring:</strong> {e.oppbevaringDn}</div>}
                                                        {e.reseptgyldighetVarighet && <div><strong>Reseptgyldighet:</strong> {parseValidityDuration(e.reseptgyldighetVarighet)}</div>}
                                                    </div>
                                                </div>}

                                                {/* ATC & Classification */}
                                                <div class='mb-3'>
                                                    <div class='small text-uppercase text-muted fw-bold mb-1'>
                                                        <Text id='dyrejournal.prescription.classification'>Klassifisering</Text>
                                                    </div>
                                                    <div class='small'>
                                                        {e.atcV && <div><strong>ATC-kode:</strong> {e.atcV} - {e.atcDn}</div>}
                                                        {e.produktInfoProdusent && <div><strong>Produsent:</strong> {e.produktInfoProdusent}</div>}
                                                    </div>
                                                </div>

                                                {/* Package Insert Link */}
                                                {e.preparatomtaleavsnittLenke && <div class='mb-2'>
                                                    <a href={e.preparatomtaleavsnittLenke} target='_blank' rel='noopener noreferrer' class='btn btn-sm btn-outline-primary'>
                                                        <i class='fa-solid fa-file-pdf me-1' />
                                                        <Text id='dyrejournal.prescription.packageInsert'>Preparatomtale (SPC)</Text>
                                                    </a>
                                                </div>}
                                            </div>
                                        </>);
                                    }}
                                    callback={this.searchDrugCallback}
                                    rightButton={newDrugTitle}
                                    rightButtonClassNames='btn-outline-secondary'
                                    rightButtonClick={this.onClickNewDrug}
                                />

                                {(drugs && drugs.length > 0) && <>
                                    {drugs.map(drug => <>
                                        <div class='d-flex flex-column border box-container mx-4 mt-2 bg-light p-3 position-relative'>

                                            {!saleIsLocked && <>
                                                <div
                                                    class='ms-2 drug position-absolute'
                                                    onClick={this.onClickRemoveDrug}
                                                    data-journal={newPrescription.id}
                                                    data-md5={drug.md5}
                                                    style='font-size: 1.4em; top: 10px; right: 10px; cursor: pointer;'
                                                >
                                                    <i class='fa-solid fa-circle-xmark text-danger' />
                                                </div>
                                            </>}

                                            <div class='d-flex flex-row align-items-start px-3 py-1'>
                                                <div class='text-muted pt-2'>
                                                    <Text id='dyrejournal.prescription.drug'>Rp.</Text>
                                                </div>
                                                <div class='flex-fill ms-2'>
                                                    <div class='d-flex flex-row align-items-center gap-2 mb-1'>
                                                        <input
                                                            type='number'
                                                            class='form-control form-control-sm'
                                                            style='width: 60px;'
                                                            name='qty'
                                                            value={drug.qty || 1}
                                                            data-md5={drug.md5}
                                                            onInput={this.onInputDrugInfo}
                                                            disabled={saleIsLocked}
                                                            min='1'
                                                        />
                                                        <span>x</span>
                                                        <input
                                                            type='text'
                                                            class='form-control form-control-sm flex-fill'
                                                            name='rpText'
                                                            value={drug.rpText || ''}
                                                            placeholder={drug.navnFormStyrke || ''}
                                                            data-md5={drug.md5}
                                                            onInput={this.onInputDrugInfo}
                                                            disabled={saleIsLocked}
                                                        />
                                                    </div>
                                                    {/* Full catalog name as wrapping text: the strength sits at the end of
                                                        navnFormStyrke, and the rpText input is too narrow on a phone to show it. */}
                                                    {drug.navnFormStyrke && <div class='small fw-semibold text-body'>{drug.navnFormStyrke}</div>}
                                                    <small class='text-muted'>
                                                        {drug.pakningsinfoPakningsstr || drug.pakningsinfoMengde} {drug.pakningsinfoEnhetPakningV}
                                                        {drug.atcDn && <> · ATC: {drug.atcDn}</>}
                                                        {drug.reseptgruppeV && <> · Grp {drug.reseptgruppeV}</>}
                                                    </small>
                                                    <PrescriptionPackageTotal
                                                        drug={drug}
                                                        className='small text-primary fw-semibold mt-1'
                                                    />
                                                </div>
                                            </div>
                                            <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                                <div class='text-muted'>
                                                    <Text id='dyrejournal.prescription.dose'>Dssn.</Text>
                                                </div>
                                                <div class='flex-fill ms-2'>
                                                    {drug.dosage && <>{drug.dosage}<br /></>}
                                                    {drug.frequency && <>{drug.frequency}<br /></>}
                                                    <small class='text-muted'>
                                                        {drug.administrasjonsveiDn && <>Adm: {drug.administrasjonsveiDn}<br /></>}
                                                        {drug.forhandsregelInntakDn && <>Merk: {drug.forhandsregelInntakDn}</>}
                                                    </small>
                                                </div>
                                            </div>
                                            {drug.preparatomtaleavsnittLenke && <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                                <div class='text-muted'>
                                                    <Text id='dyrejournal.prescription.about'>About</Text>
                                                </div>
                                                <div class='flex-fill ms-2'>
                                                    <a href={drug.preparatomtaleavsnittLenke} target='_blank' rel='noopener noreferrer'>{drug.preparatomtaleavsnittLenke}</a>
                                                </div>
                                            </div>}

                                            <div class={`d-flex flex-column justify-content-between py-1`}>
                                                <FormFieldsCompact
                                                    stores={this.props.stores}
                                                    sections={sectionDrugs}
                                                    holdingStore={prescriptionStore}
                                                    objectName={'newPrescription'}
                                                    object={drug}
                                                    hideAdmin={hideAdmin}
                                                    drawerLevel={drawerLevel}
                                                    objectUpdate={this.inputDrugInfo}
                                                    onInput={this.onInputDrugInfo}
                                                />
                                            </div>

                                        </div>
                                    </>)}
                                </>}

                            </div>
                        </div>
                    </div>


                    {isNew && <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style={`bottom: 0; left: 0;`}>
                        {!hasVeterinary && (
                            <div class='alert alert-warning py-2 mb-2 text-center' role='alert'>
                                <i class='fa-solid fa-triangle-exclamation me-1' />
                                <Text id='dyrejournal.prescription.veterinaryRequired'>Select a veterinary before creating the prescription.</Text>
                            </div>
                        )}
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.onClickCreatePrescription} disabled={!hasVeterinary}>
                            {newPrescription.id ? <Text id='dyrejournal.prescription.save'>Save prescription</Text> : <Text id='dyrejournal.prescription.create'>Create prescription</Text>}
                        </button>
                    </div>}

                </div>
            </div>
        );
    }
}

export default AddPrescription;
